import React, { useEffect, useState } from 'react'
import { Card, Image, Row, Col, Spin, Tag, Empty } from 'antd'
import { useTranslation } from 'react-i18next'
import './index.scss'
import { getShowLocationListAPI } from '@/apis/showLocation'
import errorImg from '@/assets/error.png'
import { useNavigate } from 'react-router-dom'
import { SERVER_CONF } from '@/config/server'

// 计算多边形或点集合的中心点（简单平均法）
function getCentroid(points) {
  if (!Array.isArray(points) || points.length === 0) return null
  let sumLon = 0, sumLat = 0, count = 0
  points.forEach(p => {
    if (Array.isArray(p) && p.length >= 2 && typeof p[0] === 'number' && typeof p[1] === 'number') {
      sumLon += p[0]
      sumLat += p[1]
      count++
    }
  })
  if (count === 0) return null
  return [sumLon / count, sumLat / count]
}

// 根据记录生成备用图片：优先使用 show_area -> start_area -> watch_area -> city -> fallback
function buildAutoImage(item) {
  try {
    const polyShow = Array.isArray(item.show_area) ? item.show_area : []
    const polyStart = Array.isArray(item.start_area) ? item.start_area : []
    const polyWatch = Array.isArray(item.watch_area) ? item.watch_area : []

    // 收集所有点用于计算边界
    const allPoints = [...polyShow, ...polyStart, ...polyWatch].filter(p => Array.isArray(p) && p.length >= 2)
    let bbox = null
    if (allPoints.length > 0) {
      let minLon = Infinity, maxLon = -Infinity, minLat = Infinity, maxLat = -Infinity
      allPoints.forEach(([lon, lat]) => {
        if (typeof lon === 'number' && typeof lat === 'number') {
          if (lon < minLon) minLon = lon
          if (lon > maxLon) maxLon = lon
          if (lat < minLat) minLat = lat
          if (lat > maxLat) maxLat = lat
        }
      })
      if (isFinite(minLon) && isFinite(maxLon) && isFinite(minLat) && isFinite(maxLat)) {
        // 避免单点导致除零
        if (minLon === maxLon) { minLon -= 0.0005; maxLon += 0.0005 }
        if (minLat === maxLat) { minLat -= 0.0005; maxLat += 0.0005 }
        bbox = { minLon, maxLon, minLat, maxLat }
      }
    }

    // 将经纬度映射到画布
    const width = 400, height = 240, pad = 15
    function mapPoint(lon, lat) {
      if (!bbox) return null
      const x = pad + ( (lon - bbox.minLon) / (bbox.maxLon - bbox.minLon) ) * (width - 2 * pad)
      // y 方向反转（纬度增大向上）
      const y = pad + ( (bbox.maxLat - lat) / (bbox.maxLat - bbox.minLat) ) * (height - 2 * pad)
      return [x, y]
    }
    function polyToString(poly) {
      if (!bbox) return ''
      return poly.map(p => Array.isArray(p) && p.length >=2 ? mapPoint(p[0], p[1]) : null)
                 .filter(Boolean)
                 .map(([x,y]) => `${x.toFixed(1)},${y.toFixed(1)}`)
                 .join(' ')
    }

    const showStr = polyToString(polyShow)
    const startStr = polyToString(polyStart)
    const watchStr = polyToString(polyWatch)

    const center = getCentroid(allPoints)
    const city = item.city || ''
    const label = city || (center ? 'LOCATION' : 'NO DATA')
    const subtitle = center ? `${center[0].toFixed(4)},${center[1].toFixed(4)}` : ''

    if (center || city || bbox) {
      const svgParts = []
      svgParts.push(`<?xml version='1.0' encoding='UTF-8'?>`)
      svgParts.push(`<svg xmlns='http://www.w3.org/2000/svg' width='${width}' height='${height}' viewBox='0 0 ${width} ${height}'>`)
      svgParts.push(`<defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'><stop offset='0%' stop-color='#263238'/><stop offset='100%' stop-color='#37474f'/></linearGradient></defs>`)
      svgParts.push(`<rect width='${width}' height='${height}' fill='url(#g)' rx='8' ry='8'/>`)
      // 画多边形（使用不同颜色）
      if (showStr) svgParts.push(`<polygon points='${showStr}' fill='rgba(255,152,0,0.25)' stroke='#ff9800' stroke-width='2' />`)
      if (startStr) svgParts.push(`<polygon points='${startStr}' fill='rgba(76,175,80,0.25)' stroke='#4caf50' stroke-width='2' />`)
      if (watchStr) svgParts.push(`<polygon points='${watchStr}' fill='rgba(233,30,99,0.25)' stroke='#e91e63' stroke-width='2' />`)
      // 图例
      const legendY = height - 55
      svgParts.push(`<rect x='12' y='${legendY}' width='10' height='10' fill='#ff9800'/><text x='28' y='${legendY+9}' font-size='12' fill='#fff' font-family='Arial'>Show</text>`)
      svgParts.push(`<rect x='90' y='${legendY}' width='10' height='10' fill='#4caf50'/><text x='106' y='${legendY+9}' font-size='12' fill='#fff' font-family='Arial'>Start</text>`)
      svgParts.push(`<rect x='168' y='${legendY}' width='10' height='10' fill='#e91e63'/><text x='184' y='${legendY+9}' font-size='12' fill='#fff' font-family='Arial'>Watch</text>`)
      // 标题与副标题
      svgParts.push(`<text x='50%' y='40%' font-size='28' fill='#fff' font-family='Arial' text-anchor='middle'>${label}</text>`)
      if (subtitle) svgParts.push(`<text x='50%' y='52%' font-size='14' fill='#cfd8dc' font-family='Arial' text-anchor='middle'>${subtitle}</text>`)
      svgParts.push(`<text x='50%' y='90%' font-size='12' fill='#b0bec5' font-family='Arial' text-anchor='middle'>AUTO PREVIEW</text>`)
      svgParts.push(`</svg>`)
      const svg = svgParts.join('')
      return 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svg)))
    }
  } catch (e) {
    // ignore
  }
  return errorImg
}
const BASE_SERVER_URL_LOCAL = SERVER_CONF.BASE_URL
const ShowLocationDisplay = () => {
    const { t } = useTranslation()
    const [loading, setLoading] = useState(false)
    const [list, setList] = useState([])
  const navigate = useNavigate()
    useEffect(() => {
    const fetchData = async () => {
    setLoading(true)
    try {
    const res = await getShowLocationListAPI()
    const arr = Array.isArray(res?.data) ? res.data : []
    setList(arr)
    } catch (e) {
    console.error('fetch show locations failed:', e)
    } finally {
    setLoading(false)
    }
    }
    fetchData()
    }, [])
    return (
    <Spin spinning={loading} size='large'>
    {(!list || list.length === 0) && !loading ? (
    <div style={{ padding: 40, textAlign: 'center', width: '100%' }}>
          <Empty description={t('NoData')} />
    </div>
    ) : (
    <Row gutter={[16, 16]} style={{ width: '100%' }}>
          {list.map(item => {
            const imgSrc = item.photo ? `${BASE_SERVER_URL_LOCAL}${item.photo}` : buildAutoImage(item)
            return (
              <Col xs={24} sm={12} md={8} lg={6} xl={6} key={item.id}>
                <Card
                  hoverable
                  cover={<Image height={160} src={imgSrc} alt={item.placeName} fallback={errorImg} preview={false} />}
                  style={{ borderRadius: 8 }}
                  bodyStyle={{ padding: 16 }}
                  onClick={() => navigate(`/ShowLocationDetail?id=${item.id}`)}
                >
                  <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>{item.placeName}</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {item.city && <Tag color='blue'>{item.city}</Tag>}
                    {item.visible === 1 && <Tag color='green'>{t('Visible') || '可见'}</Tag>}
                  </div>
                </Card>
              </Col>
            )
          })}
    </Row>
    )}
    </Spin>
    )
}

export default ShowLocationDisplay
